import { ChangeEvent, useEffect, useState } from 'react';

import { ApolloError } from '@apollo/client';
import { CountriesType, IApolloConnectionPageInfo } from '@netfront/common-library';
import { Button, DateRangePicker, IOption, Pagination, Select, Spinner, Table, Toolbar } from '@netfront/ui-library';
import { addMonths } from 'date-fns';
import { TranslateImprovementStatusType } from 'interfaces';
import { useRouter } from 'next/router';

import { TRANSLATE_IMPROVEMENTS_PAGE_CONSTANTS } from '../TranslateImprovementsPage.constants';

import { getTranslateImprovementsTableColumns, TRANSLATE_IMPROVEMENT_STATUS_OPTIONS } from './TranslateImprovementsTable.constants';
import { ITranslateImprovementTableData } from './TranslateImprovementsTable.interfaces';

import {
  useApproveOrDeclineTranslateImprovement,
  useExportTranslateImprovements,
  useGetCountryEnumValues,
  useGetTranslateImprovements,
  useToast,
} from '../../../../hooks';

const { pageSize: defaultPageSize } = TRANSLATE_IMPROVEMENTS_PAGE_CONSTANTS;

const ALL_LANGUAGES_OPTION_VALUE = '';
const ALL_STATUSES_OPTION_VALUE = '';

const TranslateImprovementsTable = () => {
  const { query } = useRouter();
  const { handleToastError, handleToastSuccess } = useToast();

  const { projectId } = query;

  const [translateImprovements, setTranslateImprovements] = useState<ITranslateImprovementTableData[]>([]);
  const [pageInfo, setPageInfo] = useState<IApolloConnectionPageInfo>();
  const [pageSize, setPageSize] = useState<number>(defaultPageSize);
  const [totalItems, setTotalItems] = useState<number>(0);
  const [languageOptions, setLanguageOptions] = useState<IOption[]>([
    { id: 'all', name: 'All languages', value: ALL_LANGUAGES_OPTION_VALUE },
  ]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(ALL_LANGUAGES_OPTION_VALUE);
  const [selectedStatus, setSelectedStatus] = useState<string>(ALL_STATUSES_OPTION_VALUE);
  const [processingIds, setProcessingIds] = useState<string[]>([]);
  const [dateRange, setDateRange] = useState<Date[]>([addMonths(new Date(), -1), addMonths(new Date(), 1)]);

  const handleApolloError = (error?: ApolloError) => {
    if (!error) {
      return;
    }

    handleToastError({
      error,
      shouldUseFriendlyErrorMessage: true,
    });
  };

  const { handleGetCountryEnumValues } = useGetCountryEnumValues({
    fetchPolicy: 'cache-first',
    onCompleted: ({ enumValues }) => {
      setLanguageOptions([
        { id: 'all', name: 'All languages', value: ALL_LANGUAGES_OPTION_VALUE },
        ...enumValues.map(({ key, value }) => ({ id: key, name: value, value: key })),
      ]);
    },
    onError: (error) => {
      handleApolloError(error);
    },
  });

  const {
    handleFetchMoreTranslateImprovements,
    handleGetTranslateImprovements,
    isLoading = false,
  } = useGetTranslateImprovements({
    fetchPolicy: 'no-cache',
    onCompleted: ({ items, pageInfo: newPageInfo, totalCount = 0 }) => {
      setTranslateImprovements(items);
      setPageInfo(newPageInfo);
      setTotalItems(totalCount);
    },
    onError: (error) => {
      handleApolloError(error);
    },
  });

  const { handleApproveOrDeclineTranslateImprovement } = useApproveOrDeclineTranslateImprovement({
    onError: (error) => {
      handleApolloError(error);
    },
  });

  const { handleExportTranslateImprovements, isLoading: isExportTranslateImprovementsLoading = false } = useExportTranslateImprovements({
    fetchPolicy: 'no-cache',
    onCompleted: ({ asset: { presignedUrl } }) => {
      if (!presignedUrl) {
        return;
      }

      window.open(presignedUrl, '_blank');
    },
    onError: (error) => {
      handleApolloError(error);
    },
  });

  const handleDownloadTranslateImprovements = () => {
    handleExportTranslateImprovements({
      end: dateRange[1],
      ianaTimeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      language: selectedLanguageFilter,
      projectId: String(projectId),
      start: dateRange[0],
      status: selectedStatusFilter,
    });
  };

  const handlePageSizeChange = (changedPageSize: number) => {
    setPageSize(changedPageSize);
  };

  const handleLanguageChange = ({ target: { value } }: ChangeEvent<HTMLSelectElement>) => {
    setSelectedLanguage(value);
  };

  const handleStatusChange = ({ target: { value } }: ChangeEvent<HTMLSelectElement>) => {
    setSelectedStatus(value);
  };

  const handleApproveOrDecline = async (id: string, isApproved: boolean) => {
    setProcessingIds((currentProcessingIds) => [...currentProcessingIds, id]);

    try {
      await handleApproveOrDeclineTranslateImprovement({
        id: Number(id),
        isApproved,
      });

      setTranslateImprovements((currentTranslateImprovements) =>
        currentTranslateImprovements.filter((translateImprovement) => translateImprovement.id !== id),
      );
      setTotalItems((currentTotalItems) => Math.max(0, currentTotalItems - 1));

      handleToastSuccess({
        message: `Translation improvement ${isApproved ? 'approved' : 'declined'}`,
      });
    } catch {
      // Error toast is handled by the useApproveOrDeclineTranslateImprovement onError callback
    } finally {
      setProcessingIds((currentProcessingIds) => currentProcessingIds.filter((processingId) => processingId !== id));
    }
  };

  const selectedLanguageFilter = (selectedLanguage || undefined) as CountriesType | undefined;
  const selectedStatusFilter = (selectedStatus || undefined) as TranslateImprovementStatusType | undefined;

  useEffect(() => {
    handleGetCountryEnumValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!projectId) {
      return;
    }

    handleGetTranslateImprovements({
      endDate: dateRange[1],
      first: pageSize,
      language: selectedLanguageFilter,
      projectId: String(projectId),
      startDate: dateRange[0],
      status: selectedStatusFilter,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectId, pageSize, selectedLanguage, selectedStatus, dateRange]);

  const isLoadMoreDisabled = !(pageInfo?.hasNextPage ?? false);

  return (
    <>
      <Spinner isLoading={isLoading || isExportTranslateImprovementsLoading} />

      <div className="px-4 w-full">
        <Toolbar
          additionalClassNames="c-reporting-page__table-toolbar"
          childrenEnd={<Button size="xs" text="Download" type="button" onClick={handleDownloadTranslateImprovements} />}
          childrenMiddle={
            <div className="c-reporting-page__table-toolbar__middle flex flex-1 gap-4">
              <DateRangePicker
                dateInputProps={{
                  labelText: 'Select date range',
                  id: 'id_date_selector',
                  name: 'date_range_selector',
                  type: 'text',
                  isLabelHidden: true,
                  hasPadding: false,
                }}
                selectedDates={dateRange}
                onSelected={setDateRange}
              />
              <Select
                id="translate-improvements-language-filter"
                labelText="Filter by language"
                name="translate-improvements-language-filter"
                options={languageOptions}
                value={selectedLanguage}
                onChange={handleLanguageChange}
              />
              <Select
                id="translate-improvements-status-filter"
                labelText="Filter by status"
                name="translate-improvements-status-filter"
                options={TRANSLATE_IMPROVEMENT_STATUS_OPTIONS}
                value={selectedStatus}
                onChange={handleStatusChange}
              />

            </div>
          }
        />

        <>
          {!translateImprovements.length && <p className="mt-8">There are no translate improvements available yet</p>}

          {Boolean(translateImprovements.length) && (
            <>
              <div className="mt-8">
                <Table<ITranslateImprovementTableData>
                  columns={getTranslateImprovementsTableColumns({ processingIds, onApproveOrDecline: handleApproveOrDecline })}
                  data={translateImprovements}
                />
              </div>

              {totalItems > pageSize && (
                <Pagination
                  isLoadMoreDisabled={isLoadMoreDisabled}
                  pageSize={pageSize}
                  totalItems={totalItems}
                  onPageChange={async () => {
                    const { data } = await handleFetchMoreTranslateImprovements({
                      after: pageInfo?.endCursor,
                      endDate: dateRange[1],
                      first: pageSize,
                      language: selectedLanguageFilter,
                      projectId: String(projectId),
                      startDate: dateRange[0],
                      status: selectedStatusFilter,
                    });

                    const {
                      translateImprovement: {
                        getTranslateImprovements: { items: newlyFetchedItems, pageInfo: newPageInfo, totalCount = 0 },
                      },
                    } = data;

                    setTranslateImprovements((currentTranslateImprovements) => [...currentTranslateImprovements, ...newlyFetchedItems]);
                    setPageInfo(newPageInfo);
                    setTotalItems(totalCount);
                  }}
                  onPageSizeChange={handlePageSizeChange}
                />
              )}
            </>
          )}
        </>
      </div>


    </>
  );
};

export { TranslateImprovementsTable };
